// tokenized_search.js
// ------------------------------------------------------------
// Tokenized search with a11y: autocomplete, tokens, and
// two-way sync with the Advanced Options panel.
// ------------------------------------------------------------

import $ from 'jquery';
import 'jquery-ui/ui/widgets/autocomplete';
import 'jquery-ui/themes/base/all.css';
import { XssSanitizer } from '../app/xss_sanitizer';

// Make jQuery globally available for jQuery UI
window.$ = $;
window.jQuery = $;

// ------------------------------------------------------------
// Utility Functions
// ------------------------------------------------------------

// Lightweight name guess (no digits, no '@', no key syntax)
const guessNameParts = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.includes(':') || /[@\d]/.test(s)) return null;
  let first = null; let last = null;
  if (s.includes(',')) {
    const [l, r] = s.split(/,\s*/, 2);
    if (l) last = l.trim();
    if (r) {
      const toks = r.trim().split(/\s+/).filter(Boolean);
      if (toks.length) first = toks[0];
    }
  } else {
    const toks = s.split(/\s+/).filter(Boolean);
    if (toks.length === 1) {
      last = toks[0];
    } else if (toks.length >= 2) {
      first = toks[0];
      last = toks[toks.length - 1];
    }
  }
  if (!first && !last) return null;
  return { first_name: first, last_name: last };
};

/**
 * Focus an element without scroll-jumping; falls back gracefully
 */
const safeFocus = ($el) => {
  const el = $el && $el[0];
  if (!el) return;

  try {
    el.focus({ preventScroll: true });
  } catch (_) {
    el.focus();
  }
};

/**
 * Map labels/values to boolean, or null if unknown
 */
const booleanFrom = (v) => {
  const s = String(v).trim().toLowerCase();

  if (['true', '1', 'yes', 'y', 'on', 'checked'].includes(s)) return true;
  if (['false', '0', 'no', 'n', 'off', 'unchecked'].includes(s)) return false;

  return null;
};

/**
 * Determine if a key should be treated as a date.
 * Criteria (any true):
 *  - keyMap entry type === 'date'
 *  - real key contains 'date'
 *  - real key matches /_(date|dob)$/ or is exactly 'dob'
 *  - human label contains 'date'
 *  - human label matches 'date of birth'
 */
const isDateLikeKeyName = (realKey, entry) => {
  if (!realKey) return false;
  const entryType = (entry?.type || '').toLowerCase();
  if (entryType === 'date') return true;

  const k = realKey.toLowerCase();
  const human = (entry?.human || '').toLowerCase();

  if (human.includes('date')) return true;
  if (human.includes('date of birth')) return true;

  if (k === 'dob' || k.endsWith('_dob')) return true;
  if (k.includes('date')) return true; // broader match (e.g., date_of_birth)
  if (/(^|_)date(_|$)/.test(k)) return true;

  return false;
};

/**
 * Validate a date value string.
 * Accept ONLY strict MM/DD/YYYY (two-digit month & day, four-digit year).
 */
const isValidDateString = (raw) => {
  if (!raw) return false;
  const s = String(raw).trim();
  if (!s) return false;

  const m = s.match(/^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/);
  if (!m) return false;
  const [_, mo, d, y] = m; // eslint-disable-line no-unused-vars
  return validateYMD(y, mo, d);
};

const validateYMD = (y, mo, d) => {
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  if (year < 1900 || year > 3000) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  const dt = new Date(year, month - 1, day);
  return dt.getFullYear() === year && (dt.getMonth() + 1) === month && dt.getDate() === day;
};

// Treat the literal mask string as an empty placeholder (not a real date value)
const isDatePlaceholderValue = (v) => typeof v === 'string' && v.trim().toUpperCase() === 'MM/DD/YYYY';

const parseMMDDYYYY = (s) => {
  if (!isValidDateString(s)) return null;
  const [mm, dd, yyyy] = s.split('/');
  return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
};

/**
 * Remove a trailing colon (including full-width) and surrounding spaces
 */
const normalizeKeyLabel = (s) => String(s || '').replace(/\s*[:：]\s*$/, '');

/**
 * Humanize utility - extends jQuery
 */
(function ($) {
  $.humanizeString = function (s) {
    const toTitleCase = function (string) {
      const lowers = ['A', 'An', 'The', 'And', 'But', 'Or', 'For', 'Nor', 'As', 'At', 'By', 'For', 'From', 'In', 'Into', 'Near', 'Of', 'On', 'Onto', 'To', 'With'];
      const uppers = ['Id'];

      let str = string.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

      for (let i = 0; i < lowers.length; i++) {
        str = str.replace(new RegExp('\\s' + lowers[i] + '\\s', 'g'), (txt) => txt.toLowerCase());
      }

      for (let i = 0; i < uppers.length; i++) {
        str = str.replace(new RegExp('\\b' + uppers[i] + '\\b', 'g'), uppers[i].toUpperCase());
      }

      return str;
    };

    return toTitleCase(String(s).split('_').join(' '));
  };
})(jQuery);

/**
 * Derive a display label for a field (key label)
 */
const getFieldLabel = ($el, $scope) => {
  // Priority: aria-label > data-label > <label for="id"> > humanized name
  const aria = $el.attr('aria-label');
  if (aria && aria.trim()) {
    return normalizeKeyLabel(aria.trim());
  }

  const dataLab = $el.data('label');
  if (dataLab && String(dataLab).trim()) {
    return normalizeKeyLabel(String(dataLab).trim());
  }

  const id = $el.attr('id');
  if (id) {
    const inScope = $scope ? $scope.find(`label[for="${id}"]`) : $();
    const docWide = $(`label[for="${id}"]`);
    const $lab = inScope.length ? inScope : docWide;

    if ($lab.length) {
      const txt = $lab.first().text().trim();
      if (txt) return normalizeKeyLabel(txt);
    }
  }

  // Try to find label by name attribute (case-insensitive)
  const name = $el.attr('name') || '';
  if (name) {
    // First try exact match
    const inScope = $scope ? $scope.find(`label[for="${name}"]`) : $();
    const docWide = $(`label[for="${name}"]`);
    let $lab = inScope.length ? inScope : docWide;

    // If no exact match, try case-insensitive
    if (!$lab.length) {
      const $allLabels = $scope ? $scope.find('label[for]') : $('label[for]');
      $lab = $allLabels.filter(function() {
        return $(this).attr('for').toLowerCase() === name.toLowerCase();
      });
    }

    if ($lab.length) {
      const txt = $lab.first().text().trim();
      if (txt) return normalizeKeyLabel(txt);
    }
  }

  // Preserve all-uppercase acronyms like NCPDP
  if (name && name === name.toUpperCase() && name.length > 1) {
    return name;
  }

  // Check if the name is an all-caps acronym before humanizing
  if (name && /^[A-Z]+$/.test(name) && name.length > 1) {
    return name;
  }

  return $.humanizeString(name);
};

/**
 * Centralized logic for getting human-readable checkbox label text
 * Prioritizes checkbox-specific labels over form group labels
 */
const getCheckboxLabel = ($checkbox, displayKeyLabel = null) => {
  const checkboxId = $checkbox.attr('id');
  const checkboxName = $checkbox.attr('name');
  let $label = $();

  if (checkboxId) {
    const $allLabels = $(`label[for="${checkboxId}"]`);

    // Look for the checkbox-specific label (usually has custom-control-label class)
    $label = $allLabels.filter('.custom-control-label').first();

    // If no custom-control-label, find the one that's NOT the displayKeyLabel
    if (!$label.length) {
      $label = $allLabels.filter(function() {
        const text = $(this).text().trim();
        return text !== displayKeyLabel && !text.includes(':');
      }).first();
    }

    // Fallback to any label for this id
    if (!$label.length) {
      $label = $allLabels.first();
    }
  }

  // Fallback: look for a label by name attribute
  if (!$label.length && checkboxName) {
    const $allLabels = $(`label[for="${checkboxName}"]`);

    $label = $allLabels.filter('.custom-control-label').first();

    if (!$label.length) {
      $label = $allLabels.filter(function() {
        const text = $(this).text().trim();
        return text !== displayKeyLabel && !text.includes(':');
      }).first();
    }
  }

  if ($label.length) {
    const labelText = $label.text().trim();
    if (labelText && labelText !== displayKeyLabel) {
      return labelText;
    }
  }

  return 'Yes'; // fallback
};

/**
 * Centralized logic for getting human-readable values for any field type
 */
const getHumanReadableValue = (field, value, keyMap = null, realKey = null) => {
  const $field = $(field);
  const type = field.type?.toLowerCase() || field.nodeName.toLowerCase();

  switch (type) {
    case 'select-one': {
      const option = $field.find(`option[value="${value}"]`);
      return option.length ? option.text().trim() : value;
    }

    case 'checkbox': {
      const b = booleanFrom(value);
      if (b === true) {
        // Use keyMap entry if available, otherwise get from DOM
        if (keyMap && realKey) {
          const entry = keyMap[realKey];
          const checkboxValueEntry = entry?.values.find(v => v.value === 'true');
          if (checkboxValueEntry) {
            return checkboxValueEntry.label;
          }
        }

        // Fallback to DOM lookup
        return getCheckboxLabel($field);
      }
      return b === false ? 'No' : String(value);
    }

    case 'radio': {
      // Use keyMap entry if available
      if (keyMap && realKey) {
        const entry = keyMap[realKey];
        const hit = entry?.values.find(v => String(v.value) === String(value));
        if (hit) return hit.label;
      }

      // Fallback to DOM lookup
      const name = $field.attr('name');
      if (name) {
        const $radioWithValue = $field.closest('#search_options, form')
          .find(`input[type="radio"][name="${name}"][value="${value}"]`);

        if ($radioWithValue.length) {
          const radioId = $radioWithValue.attr('id');
          if (radioId) {
            const $label = $(`label[for="${radioId}"]`);
            if ($label.length) {
              return $label.text().trim();
            }
          }
        }
      }

      // Fallback: check if it's underscore-separated and humanize it
      if (String(value).includes('_')) {
        return $.humanizeString(value);
      }

      return value;
    }

    case 'text':
    default:
      return /[A-Z]+/.test(value) ? value : String(value).split('_').join(' ');
  }
};

// ------------------------------------------------------------
// SearchToken Class
// ------------------------------------------------------------
class SearchToken {
  constructor({
    key,
    value,
    field,
    source = 'input',
    container = $('#tokenized_filters'),
    displayLabel = null,
    displayKeyLabel = null
  }) {
    this.key = key.trim(); // REAL key (name attr)
    this.humanKey = displayKeyLabel || $.humanizeString(this.key); // Human-readable key
    this.relatedField = field; // jQuery element
    this.source = source; // 'input' | 'advanced'
    this.container = container && container.length ? container : $('#tokenized_filters');

    this.value = XssSanitizer.sanitize(value).trim();
    this.humanValue = displayLabel != null
      ? XssSanitizer.sanitize(displayLabel).trim()
      : this.computeHumanValue(this.value);

    this._createTokenDOM();
    this.addToDOM();
    this.bindRemoveButton();
    this.syncRelatedField(); // reflect into Advanced panel if created from input
  }

  computeHumanValue(value) {
    const field = this.relatedField.get(0);
    if (!field) return value;

    return getHumanReadableValue(field, value);
  }

  _createTokenDOM() {
    this.node = $(`
      <span class="token"
            role="listitem"
            tabindex="-1"
            data-key="${XssSanitizer.sanitize_dom_text(this.key.toLowerCase())}"
            data-human-key="${XssSanitizer.sanitize_dom_text(this.humanKey)}"
            data-human-value="${XssSanitizer.sanitize_dom_text(this.humanValue)}"
            aria-label="Filter ${XssSanitizer.sanitize_dom_text(this.humanKey)}: ${XssSanitizer.sanitize_dom_text(this.humanValue)}.">
        <span class="token-key" title="${XssSanitizer.sanitize_dom_text(this.humanKey)}">
          <span class="sr-only">Search Criteria: </span>
          <span class="token-visible-key">${XssSanitizer.sanitize_dom_text(this.humanKey)}</span>
        </span>
        <span class="sr-only">Search Criteria value: </span>
        <span class="token-value" title="${XssSanitizer.sanitize_dom_text(this.humanValue)}">
          <span class="token-visible-value">${XssSanitizer.sanitize_dom_text(this.humanValue)}</span>
        </span>
        <button class="btn btn-primary remove-token" type="button" tabindex="-1"
                aria-label="Remove filter ${XssSanitizer.sanitize_dom_text(this.humanKey)}: ${XssSanitizer.sanitize_dom_text(this.humanValue)}">
          <span aria-hidden="true" class="remove-token-icon"><i class="fa fa-times"></i></span>
        </button>
      </span>
    `);
  }

  addToDOM() {
    if (!this.container.length) {
      console.error('Token container #tokenized_filters not found!');
      return;
    }

    this.container.append(this.node);
    this.container.trigger('tokenized_search.token_added', {
      tokenEl: this.node.get(0),
      focus: false
    });
  }

  bindRemoveButton() {
    this.node.find('.remove-token').on('click', () => this.remove());
  }

  remove() {
    this.clearRelatedField(); // clear field in Advanced Options if applicable
    const el = this.node.get(0);
    const container = this.node.closest('#tokenized_filters');

    this.node.remove();
    container.trigger('tokenized_search.token_removed', { removedEl: el });

    const field = this.relatedField.get(0);
    if (field) {
      $(field).trigger('tokenized_search.token_removed');
    }
  }

  syncRelatedField() {
    if (this.source !== 'input') return;

    let field = this.relatedField.get(0);
    if (!field) return;

    let $field = $(field);
    let type = (field.type || field.nodeName).toLowerCase();

    // If we accidentally captured a hidden input (Rails checkbox helper), find the visible control
    if (type === 'hidden' && $field.attr('name')) {
      const name = $field.attr('name');
      const $panel = $field.closest('#search_options');

      $field = $panel.find(`input[type="checkbox"][name="${name}"]`);
      if (!$field.length) $field = $panel.find(`input[type="radio"][name="${name}"]`);
      if (!$field.length) $field = $panel.find(`select[name="${name}"]`);
      if (!$field.length) $field = $panel.find(`[name="${name}"]:not([type="hidden"])`);
      if (!$field.length) return;

      type = ($field[0].type || $field[0].nodeName).toLowerCase();
    }

    try {
      switch (type) {
        case 'checkbox': {
          const b = booleanFrom(this.value);
          const checked = (b === null)
            ? ['true', 'yes', '1', 'on'].includes(String(this.value).toLowerCase())
            : b;
          $field.prop('checked', checked).trigger('input').trigger('change');
          break;
        }
        case 'radio': {
          const name = $field.attr('name');
          if (!name) return;

          const $group = $field.closest('#search_options').find(`input[type="radio"][name="${name}"]`);
          $group.prop('checked', false);

          const $match = $group.filter(`[value="${this.value}"]`);
          if ($match.length) {
            $match.prop('checked', true);
          }
          $match.trigger('input').trigger('change');
          break;
        }
        case 'select-one':
          $field.val(this.value).trigger('input').trigger('change');
          break;
        default:
          $field.val(this.value).trigger('input').trigger('change'); // text-like
      }
    } catch (e) {
      // no-op
    }
  }

  clearRelatedField() {
    const $fieldSet = this.relatedField;
    if (!$fieldSet || !$fieldSet.length) return false;

    const inPanel = $fieldSet.closest('#search_options').length > 0;
    if (!inPanel) return false;

    // Resolve the right control (avoid hidden)
    let $field = $fieldSet;
    let nodeName = $field[0].nodeName.toLowerCase();
    let typeAttr = ($field.attr('type') || '').toLowerCase();

    if (typeAttr === 'hidden' && $field.attr('name')) {
      const name = $field.attr('name');
      const $panel = $field.closest('#search_options');
      $field = $panel.find(`[name="${name}"]:not([type="hidden"])`);

      if (!$field.length) return false;

      nodeName = $field[0].nodeName.toLowerCase();
      typeAttr = ($field.attr('type') || '').toLowerCase();
    }

    const type = nodeName === 'select' ? 'select-one' : typeAttr;
    let cleared = false;

    switch (type) {
      case 'checkbox':
        $field.prop('checked', false).trigger('input').trigger('change');
        return true;

      case 'radio': {
        const name = $field.attr('name');
        if (name) {
          const $group = $field.closest('#search_options').find(`input[type="radio"][name="${name}"]`);
          const $wasChecked = $group.filter(':checked');
          $group.prop('checked', false);
          ($wasChecked.length ? $wasChecked : $group.first()).trigger('input').trigger('change');
        }
        return true;
      }

      case 'select-one': {
        const $select = $field;
        const $blank = $select.find('option[value=""], option:not([value])').first();

        if ($blank.length) {
          $select.val($blank.val());
        } else {
          $select.prop('selectedIndex', 0);
        }
        cleared = true;
        break;
      }

      default:
        // If date-like, blank entirely (avoid leaving mask like MM/DD/YYYY)
        const fname = $field.attr('name');
        if (isDateLikeKeyName(fname, this.keyMap?.[fname])) {
          if (isDatePlaceholderValue($field.val()) || !isValidDateString($field.val())) {
            $field.val('');
          } else {
            $field.val('');
          }
        } else {
          $field.val('');
        }
        cleared = true;
    }

    if (cleared) {
      $field.trigger('input').trigger('change');
    }

    return cleared;
  }
}

// ------------------------------------------------------------
// TokenizedSearch Class
// ------------------------------------------------------------
class TokenizedSearch {
  constructor(inputNode) {
    this.inputNode = $(inputNode);
    this.formNode = this.inputNode.closest('form');
    this.wrapper = this.inputNode.closest('.search-input-wrapper');
    this.tokenBox = this.wrapper.find('#tokenized_filters');
    this.toggleBtn = this.wrapper.find('#search_option_toggle');
    this.submitBtn = this.wrapper.find('#search-bar-search-button');

    this.pendingRealKey = null;
    this._refocusToggleReason = null;

    this._setupARIA();
    this._setupTokenWrapper();
    this._resolveAdvancedPanel();
    this._collectAdvancedOptions();

    this.initAutocomplete();
    this.bindA11yBehavior();
    this._bindInputHandlers();
    this._bindAdvancedPanelHandlers();
    this._bindFormSubmitSanitize();
    this._bindFormSubmitSearchBoxParam();
    this._bindFormSubmitNameGuess();
  }

  _setupARIA() {
    if (!this.wrapper.attr('role')) {
      this.wrapper.attr({
        role: 'group',
        'aria-label': 'Search with advanced options'
      });
    }

    this.tokenBox.attr({
      role: 'list',
      'aria-label': 'Active search filters'
    });

    if (this.toggleBtn.length) this.toggleBtn.attr('tabindex', '0');
    if (this.submitBtn.length) this.submitBtn.attr('tabindex', '0');
  }

  _setupTokenWrapper() {
    if (!this.inputNode.parent().hasClass('token_input_wrapper')) {
      this.inputNode.wrap('<div class="token_input_wrapper"></div>');
    }
  }

  _resolveAdvancedPanel() {
    this.panelNode = this.resolveAdvancedPanel();
    if (!this.panelNode || !this.panelNode.length) {
      this.panelNode = this.formNode.find('#search_options').first();
    }
  }

  _collectAdvancedOptions() {
    const { keyMap, humanToReal, keysForDisplay } = this.collectAdvancedOptions();
    this.keyMap = keyMap;
    this.humanToReal = humanToReal;
    this.keysForDisplay = keysForDisplay;
  }

  _bindInputHandlers() {
    this.inputNode.on('keydown', (e) => this.processInput(e));
    // Reset name guess applied flag when user edits text
    this.inputNode.on('input.tsNameGuessFlag', () => { this._nameGuessApplied = false; });
    // Ensure click on submit button refreshes name guess before submission
    if (this.submitBtn && this.submitBtn.length) {
      this.submitBtn.off('click.tsNameGuess').on('click.tsNameGuess', (e) => {
        if (this._maybeApplyNameGuess()) {
          // name guess replaced fields; allow normal submit after field mutation
        }
        // If button is not a submit button, force submit
        const typeAttr = (this.submitBtn.attr('type') || '').toLowerCase();
        if (typeAttr !== 'submit' && this.formNode && this.formNode.length) {
          e.preventDefault();
          this.formNode.trigger('submit');
        }
      });
    }
    // Guard: run name guess just before submit if not already applied
    if (this.formNode && this.formNode.length) {
      this.formNode.off('submit.tsNameGuessPre').on('submit.tsNameGuessPre', () => {
        this._maybeApplyNameGuess();
      });
    }
  }

  _bindAdvancedPanelHandlers() {
    if (this.panelNode && this.panelNode.length) {
      this.panelNode.on('change', 'input, select', (e) => this.tokenizeFormSync(e));
      this.panelNode.on('input', 'input, select', (e) => this.tokenizeFormSync(e));
      this.panelNode.find('input, select').each((_, el) => this.tokenizeFormSync({ target: el }));
    }
  }

  _bindFormSubmitSanitize() {
    if (!this.formNode || !this.formNode.length) return;
    // Ensure only one handler per form
    this.formNode.off('submit.tsDateSanitize').on('submit.tsDateSanitize', () => {
      if (!this.keyMap) return;
      Object.keys(this.keyMap).forEach((realKey) => {
        const entry = this.keyMap[realKey];
        if (!isDateLikeKeyName(realKey, entry)) return;
        const $fields = this._findFieldForKey(realKey);
        $fields.each((_, f) => {
          const $f = $(f);
          const val = $f.val();
          if (isDatePlaceholderValue(val) || (val && !isValidDateString(val))) {
            $f.val('');
          }
        });
      });
    });
  }

  _bindFormSubmitSearchBoxParam() {
    if (!this.formNode || !this.formNode.length) return;
    // Ensure only one handler per form for free-text capture
    this.formNode.off('submit.tsFreeText').on('submit.tsFreeText', () => {
      const raw = (this.inputNode.val() || '').trim();
      const $existingHidden = this.formNode.find('input[type="hidden"][name="search_box"]');

      // If input cleared, remove any previously added hidden field so stale value isn't submitted
      if (!raw) {
        if ($existingHidden.length) $existingHidden.remove();
        return; // nothing to add
      }

      // If the visible input already has the correct name, let the browser submit it (it will submit current value)
      if (this.inputNode.attr('name') === 'search_box') return;

      // Reuse existing hidden input if present; otherwise create one.
      if (!$existingHidden.length) {
        const $hidden = $(
          `<input type="hidden" name="search_box" value="${XssSanitizer.sanitize_dom_text(raw)}">`
        );
        this.formNode.append($hidden);
      } else {
        $existingHidden.val(raw);
      }
    });

    // Also clear any hidden free-text param immediately when user clears the visible input
    this.inputNode.off('input.tsFreeTextClear').on('input.tsFreeTextClear', () => {
      const val = (this.inputNode.val() || '').trim();
      if (!val) {
        // Remove hidden free-text param and any name guess hidden fields when user clears input
        this.formNode.find('input[type="hidden"][name="search_box"]').remove();
        this.formNode.find('input[type="hidden"].ts-name-guess[name="first_name"], input[type="hidden"].ts-name-guess[name="last_name"], input[type="hidden"].ts-name-guess[name="name"]').remove();
      }
    });
  }

  // Legacy behavior parity: infer first_name / last_name / name from raw free-text if those
  // fields exist in the form (or are expected) but are blank on submit.
  _bindFormSubmitNameGuess() {
    if (!this.formNode || !this.formNode.length) return;
    this.formNode.off('submit.tsNameGuess').on('submit.tsNameGuess', () => {
      const raw = (this.inputNode.val() || '').trim();
      if (!raw) {
        // Input cleared before submit: ensure we don't submit stale inferred name fields
        this.formNode.find('input[type="hidden"].ts-name-guess[name="first_name"], input[type="hidden"].ts-name-guess[name="last_name"], input[type="hidden"].ts-name-guess[name="name"]').remove();
        return; // nothing to infer
      }

      const parts = raw.split(/\s+/).filter(Boolean);
  const singleWord = parts.length === 1;
  const first = parts[0] || raw; // for single word treat as first_name
  const last = parts.length > 1 ? parts[1] : undefined;

      // We'll only add hidden inputs if the form has corresponding fields (by name) and they are blank OR the form serializes them blank.
      // Build a quick lookup of serialized values.
      const serialized = this.formNode.serializeArray().reduce((m, f) => { m[f.name] = f.value; return m; }, {});

      const addOrUpdateHidden = (fieldName, value) => {
        if (!value) return;
        let $hidden = this.formNode.find(`input[type="hidden"].ts-name-guess[name="${fieldName}"]`);
        if (!$hidden.length) {
          $hidden = $(`<input type="hidden" class="ts-name-guess" name="${fieldName}" />`);
          this.formNode.append($hidden);
        }
        $hidden.val(XssSanitizer.sanitize(value));
      };

      if (singleWord) {
        // Always ensure a first_name hidden param for single name searches.
        const visibleFirst = this.formNode.find('[name="first_name"]').not('input.ts-name-guess');
        if (!visibleFirst.length || !visibleFirst.val()) {
          addOrUpdateHidden('first_name', first);
        }
        // Only include composite name if no visible first_name field exists.
        if (!visibleFirst.length) {
          addOrUpdateHidden('name', raw);
        }
      } else {
        // Multi-word: first token -> first_name, second token -> last_name, and composite name if no visible name field.
        const visibleFirst = this.formNode.find('[name="first_name"]').not('input.ts-name-guess');
        const visibleLast = this.formNode.find('[name="last_name"]').not('input.ts-name-guess');
        if (!visibleFirst.length || !visibleFirst.val()) addOrUpdateHidden('first_name', first);
        if (last && (!visibleLast.length || !visibleLast.val())) addOrUpdateHidden('last_name', last);
        // Provide full raw as composite if no explicit first_name field.
        if (!visibleFirst.length) addOrUpdateHidden('name', raw);
      }
    });
  }

  resolveAdvancedPanel() {
    const $toggle = this.wrapper.find('#search_option_toggle,[data-bs-target],[data-target]').first();
    if (!$toggle.length) return $();

    let target = $toggle.attr('aria-controls') ||
                 $toggle.attr('data-bs-target') ||
                 $toggle.attr('data-target');

    if (!target) return $();

    const id = String(target).startsWith('#') ? String(target).slice(1) : String(target);
    return $(`#${id}`);
  }

  collectAdvancedOptions() {
    const keyMap = {};
    const humanToReal = {};
    const seenNames = new Set();

    if (!this.panelNode || !this.panelNode.length) {
      return { keyMap, humanToReal, keysForDisplay: [] };
    }

    // Helper: find a group label for radios by shared name
    const findRadioGroupLabel = ($anyRadio) => {
      const name = $anyRadio.attr('name') || '';
      if (!name) return $.humanizeString(name);

      const $grp = $anyRadio.closest('.form-group, .form-group.row, .row');
      if ($grp.length) {
        const $labels = $grp.find('label');
        for (let i = 0; i < $labels.length; i++) {
          const $lab = $($labels[i]);
          const forAttr = $lab.attr('for');

          if (!forAttr || forAttr === name) {
            const text = $lab.text().trim();
            if (text) return normalizeKeyLabel(text);
          }
        }
      }

      const $formLab = this.formNode.find(`label[for="${name}"]`).first();
      if ($formLab.length) {
        const t = $formLab.text().trim();
        if (t) return normalizeKeyLabel(t);
      }

      const aria = $anyRadio.attr('aria-label');
      if (aria && aria.trim()) return normalizeKeyLabel(aria.trim());

      return $.humanizeString(name);
    };

    this.panelNode.find('input, select').each((_, el) => {
      const $el = $(el);
      const name = $el.attr('name');

      if (!name || seenNames.has(name)) return;

      // Skip hidden/system fields
      const typeAttr = ($el.attr('type') || '').toLowerCase();
      const nodeName = el.nodeName.toLowerCase();

      if (typeAttr === 'hidden' ||
          (nodeName === 'input' && ['hidden', 'submit', 'button', 'image'].includes(typeAttr))) {
        return;
      }

      seenNames.add(name);

      // Determine control type
      let type = ($el.attr('type') || el.nodeName).toLowerCase();
      if (type === 'text') {
        const inputTypeAttr = ($el.attr('type') || '').toLowerCase();
        if (inputTypeAttr === 'date') type = 'date';
      } else if (type === 'date') {
        // keep as is
      }
      const entry = { human: '', type, values: [] };

      if ($el.is('select')) {
        entry.type = 'select-one';
        entry.human = getFieldLabel($el, this.formNode);

        $el.find('option').each((__, opt) => {
          const $opt = $(opt);
          const val = $opt.val();
          const label = $opt.text().trim();

          if (String(val).length) {
            entry.values.push({ value: val, label });
          }
        });
      } else if (type === 'checkbox') {
        entry.human = getFieldLabel($el, this.formNode);

        // Use centralized checkbox label logic
        const checkboxLabel = getCheckboxLabel($el, entry.human);
        entry.values.push({ value: 'true', label: checkboxLabel });
      } else if (type === 'radio') {
        entry.human = findRadioGroupLabel($el);

        // Enumerate all radios in the group to get values/labels
        this.panelNode.find(`input[type="radio"][name="${name}"]`).each((__, r) => {
          const $r = $(r);
          const val = $r.val();
          let label = $r.data('label') || null;

          if (!label) {
            const rid = $r.attr('id');
            if (rid) {
              const $lab = this.formNode.find(`label[for="${rid}"]`);
              if ($lab.length) {
                label = $lab.first().text().trim();
              }
            }
          }

          entry.values.push({ value: val, label: label || val });
        });
      } else {
        // Text-like inputs
        entry.human = getFieldLabel($el, this.formNode);
      }

      keyMap[name] = entry;

      // Map display label → real key (lowercased)
      if (entry.human) {
        humanToReal[entry.human.toLowerCase()] = name;
      }

      // Back-compat: also map humanized name in case it differs from label
      humanToReal[$.humanizeString(name).toLowerCase()] = name;
    });

    const keysForDisplay = Object.keys(keyMap).map((realKey) => ({
      label: keyMap[realKey].human,
      value: keyMap[realKey].human,
      realKey
    }));

    return { keyMap, humanToReal, keysForDisplay };
  }

  resolveRealKey(keyOrHuman) {
    if (this.keyMap[keyOrHuman]) return keyOrHuman; // already a real key

    const lk = String(keyOrHuman || '').toLowerCase();
    return this.humanToReal[lk] || null;
  }

  resolveRealValueFromLabel(realKey, typedLabel) {
    const entry = this.keyMap[realKey];
    if (!entry) return null;

    const found = entry.values.find(v =>
      v.label.toLowerCase() === String(typedLabel).toLowerCase()
    );

    if (found) return found.value;

    if ((entry.type || '').toLowerCase() === 'checkbox') {
      const b = booleanFrom(typedLabel);
      if (b !== null) return b ? 'true' : 'false';
    }

    return null;
  }

  // ---------- Autocomplete ----------
  initAutocomplete() {
    this._bindTabBypass();
    this._setupAutocompleteWidget();
    this._setupAutocompleteARIA();
    this._bindArrowDownHandler();
  }

  _bindTabBypass() {
    // Allow Tab to move focus even when menu is open
    this.inputNode.on('keydown.tsTabBypass', (e) => {
      if (e.key === 'Tab') {
        let isOpen = false;
        try {
          const $menu = this.inputNode.autocomplete('widget');
          isOpen = $menu && $menu.is(':visible');
        } catch (_) {}

        if (isOpen) {
          try {
            this.inputNode.autocomplete('close');
          } catch (_) {}
          e.stopImmediatePropagation();
        }
      }
    });
  }

  _setupAutocompleteWidget() {
    this.inputNode.autocomplete({
      minLength: 0,
      delay: 0,
      autoFocus: true,
      source: (request, response) => this._handleAutocompleteSource(request, response),
      focus: () => false,
      select: (event, ui) => this._handleAutocompleteSelect(event, ui),
    });

    // Custom render item
    this.inputNode.autocomplete('instance')._renderItem = function (ul, item) {
      return $('<li role="option">').append(`<div>${item.label}</div>`).appendTo(ul);
    };
  }

  _handleAutocompleteSource(request, response) {
    let term = request.term;
    const currentVal = this.inputNode.val();

    // Prevent showing suggestions automatically on an empty term unless explicitly allowed
    if (String(term).trim() === '') {
      if (this._allowEmptySearch) {
        // one-time allowance then reset
        this._allowEmptySearch = false;
      } else {
        response([]);
        return;
      }
    }

    if (currentVal && currentVal.includes(':')) {
      term = currentVal;
    }

    const colonIndex = term.indexOf(':');

    if (colonIndex === -1) {
      // Suggest KEYS by display label
      const filtered = this.keysForDisplay.filter(k =>
        k.label.toLowerCase().includes(term.toLowerCase())
      );
      response(filtered);
    } else {
      // Suggest VALUES for chosen key
      const friendlyKey = term.slice(0, colonIndex).trim();
      const realKey = this.resolveRealKey(friendlyKey);

      if (!realKey) return response([]);

      const entry = this.keyMap[realKey];
      if (!entry) return response([]);

      const valPart = term.slice(colonIndex + 1).trim().toLowerCase();

      // Regular value suggestions for all fields
      const valueItems = entry.values
        .filter(v => v.label.toLowerCase().includes(valPart))
        .map(v => ({
          label: v.label,
          value: v.label,
          realValue: v.value,
          realKey
        }));

      response(valueItems);
    }
  }

  _handleAutocompleteSelect(event, ui) {
    const current = this.inputNode.val();
    const hasColon = current.includes(':');

    // KEY chosen
    if (ui.item.realKey && !hasColon) {
      this.pendingRealKey = ui.item.realKey;
      this.inputNode.val(ui.item.label + ':');
      safeFocus(this.inputNode);
      this.inputNode.autocomplete('search', this.inputNode.val());
      return false;
    }

    // VALUE chosen
    if (ui.item.realValue && (hasColon || this.pendingRealKey)) {
      const realKey = ui.item.realKey || this.pendingRealKey;
      const $field = this._findFieldForKey(realKey);

      const tok = new SearchToken({
        key: realKey,
        value: ui.item.realValue,
        field: $field.length ? $field : this.inputNode,
        source: 'input',
        container: this.tokenBox,
        displayLabel: ui.item.label,               // value label (e.g., "Yes")
        displayKeyLabel: this.keyMap[realKey]?.human // key label (from <label>)
      });

      this.enhanceTokenA11y($(tok.node));
      this.pendingRealKey = null;
      this.inputNode.val('');
      this.inputNode.trigger('focus');
      return false;
    }

    this.inputNode.val(ui.item.value);
    return false;
  }

  _findFieldForKey(realKey) {
    // Pick the right control (avoid Rails hidden inputs)
    const entry = this.keyMap[realKey];

    if (entry) {
      const t = (entry.type || '').toLowerCase();
      if (t === 'checkbox') return this.panelNode.find(`input[type="checkbox"][name="${realKey}"]`);
      if (t === 'radio') return this.panelNode.find(`input[type="radio"][name="${realKey}"]`);
      if (t === 'select-one') return this.panelNode.find(`select[name="${realKey}"]`);
      return this.panelNode.find(`[name="${realKey}"]:not([type="hidden"])`);
    }

    return this.panelNode.find(`[name="${realKey}"]:not([type="hidden"])`);
  }

  _setupAutocompleteARIA() {
    // ARIA for combobox
    const $menu = this.inputNode.autocomplete('widget');
    $menu.uniqueId();

    this.inputNode.attr({
      role: 'combobox',
      'aria-autocomplete': 'list',
      'aria-haspopup': 'listbox',
      'aria-controls': $menu.attr('id'),
      'aria-expanded': 'false',
    });

    this.inputNode.on('autocompleteopen', () =>
      this.inputNode.attr('aria-expanded', 'true')
    );

    this.inputNode.on('autocompleteclose', () =>
      this.inputNode.attr('aria-expanded', 'false')
    );
  }

  _bindArrowDownHandler() {
    // ArrowDown opens suggestions only if closed
    this.inputNode.off('keydown.tsOpenOnArrow').on('keydown.tsOpenOnArrow', (e) => {
      if (e.key === 'ArrowDown') {
        let isOpen = false;
        try {
          const $m = this.inputNode.autocomplete('widget');
          isOpen = $m && $m.is(':visible');
        } catch (_) {}

        if (!isOpen) {
          // Allow one empty search if user explicitly presses ArrowDown
          this._allowEmptySearch = true;
          this.inputNode.autocomplete('search', this.inputNode.val());
        }
      }
    });
  }

  // ---------- Keyboard & Focus Management ----------
  bindA11yBehavior() {
    this._bindAdvancedPanelBehavior();
    this._bindInputFocusBehavior();
    this._bindTokenNavigation();
    this._bindTabOrder();
  }

  _bindAdvancedPanelBehavior() {
    if (this.panelNode && this.panelNode.length && this.toggleBtn && this.toggleBtn.length) {
      this.toggleBtn
        .off('mousedown.tsRefocusFlag keydown.tsRefocusFlag')
        .on('mousedown.tsRefocusFlag', () => {
          this._refocusToggleReason = 'toggle';
        })
        .on('keydown.tsRefocusFlag', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            this._refocusToggleReason = 'toggle';
          }
        });

      this.panelNode.on('shown.bs.collapse', () => {
        const $first = this.panelNode.find(':input:visible:not([disabled])').first();
        if ($first.length) $first.trigger('focus');
      });

      this.panelNode.on('hidden.bs.collapse', () => {
        if (this._refocusToggleReason === 'toggle' || this._refocusToggleReason === 'escape') {
          this.toggleBtn.trigger('focus');
        }
        this._refocusToggleReason = null;
      });

      this.panelNode.on('keydown', (e) => {
        if (e.key === 'Escape') {
          this._refocusToggleReason = 'escape';
          if (typeof this.panelNode.collapse === 'function') {
            this.panelNode.collapse('hide');
          } else {
            this.toggleBtn.get(0)?.click();
          }
          e.stopPropagation();
          e.preventDefault();
        }
      });
    }
  }

  _bindInputFocusBehavior() {
    this.inputNode
      .off('mousedown.tsClearRefocus focus.tsClearRefocus keydown.tsClearRefocus')
      .on('mousedown.tsClearRefocus focus.tsClearRefocus keydown.tsClearRefocus', () => {
        this._refocusToggleReason = null;
      });

    this.wrapper
      .off('mousedown.tsClearRefocusWrapper')
      .on('mousedown.tsClearRefocusWrapper', (e) => {
        if (!$(e.target).closest('#search_options, #search_option_toggle').length) {
          this._refocusToggleReason = null;
        }
      });

    this.inputNode.on('keydown.tsCaretIntoTokens', (e) => {
      const el = this.inputNode.get(0);
      const atStart = el &&
                     typeof el.selectionStart === 'number' &&
                     el.selectionStart === 0 &&
                     el.selectionEnd === 0;

      const tokens = this.getTokens();
      if (!tokens.length) return;

      if ((e.key === 'ArrowLeft' || e.key === 'Backspace') && atStart) {
        e.preventDefault();
        this.setActiveToken($(tokens.get(tokens.length - 1)), { focus: true });
      }
    });
  }

  _bindTokenNavigation() {
    this.ensureActiveToken();

    this.tokenBox.on('tokenized_search.token_added', (_, payload = {}) => {
      const tokens = this.getTokens();
      const $currentActive = tokens.filter('[tabindex="0"]').first();

      if (payload.focus && payload.tokenEl) {
        this.setActiveToken($(payload.tokenEl), { focus: true });
        return;
      }

      if (!$currentActive.length && tokens.length) {
        this.setActiveToken(tokens.first(), { focus: false });
      }
    });

    this.tokenBox.on('tokenized_search.token_removed', () => {
      this.ensureActiveToken({ prefer: 'first' });
      this._enforceThresholdDateRange();
    });

    this.tokenBox.on('keydown.tsTokenNav', '.token', (e) => {
      this._handleTokenKeydown(e);
    });

    this.tokenBox.on('mousedown click', '.token', (e) => {
      this.setActiveToken($(e.currentTarget), { focus: false });
    });
  }

  _handleTokenKeydown(e) {
    const $current = $(e.currentTarget);

    if (e.key === 'Tab') {
      const tokens = this.getTokens();
      const idx = tokens.index($current);

      if (e.shiftKey) {
        if (idx > 0) {
          e.preventDefault();
          this.setActiveToken($(tokens.get(idx - 1)), { focus: true });
        }
      } else {
        if (idx < tokens.length - 1) {
          e.preventDefault();
          this.setActiveToken($(tokens.get(idx + 1)), { focus: true });
        }
      }
      return;
    }

    if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Backspace', 'Delete', 'Escape'].includes(e.key)) {
      e.preventDefault();
      const tokens = this.getTokens();
      const idx = tokens.index($current);

      switch (e.key) {
        case 'ArrowLeft':
          if (idx > 0) {
            this.setActiveToken($(tokens.get(idx - 1)), { focus: true });
          } else {
            this.inputNode.trigger('focus');
          }
          break;

        case 'ArrowRight':
          if (idx < tokens.length - 1) {
            this.setActiveToken($(tokens.get(idx + 1)), { focus: true });
          } else {
            this.inputNode.trigger('focus');
          }
          break;

        case 'Home':
          if (tokens.length) {
            this.setActiveToken($(tokens.get(0)), { focus: true });
          }
          break;

        case 'End':
          if (tokens.length) {
            this.setActiveToken($(tokens.get(tokens.length - 1)), { focus: true });
          }
          break;

        case 'Backspace':
        case 'Delete': {
          const prev = idx > 0 ? $(tokens.get(idx - 1)) : null;
          $current.find('.remove-token').trigger('click');

          if (prev && prev.length) {
            this.setActiveToken(prev, { focus: true });
          } else {
            this.inputNode.trigger('focus');
          }
          break;
        }

        case 'Escape':
          this.inputNode.trigger('focus');
          break;
      }
    }
  }

  _bindTabOrder() {
    if (this.submitBtn && this.submitBtn.length) {
      this.submitBtn.off('keydown.tsPrimeFirstToken').on('keydown.tsPrimeFirstToken', (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          const tokens = this.getTokens();
          if (tokens.length) {
            this.setActiveToken(tokens.first(), { focus: false });
          }
        }
      });
    }

    this.inputNode.off('keydown.tsPrimeLastToken').on('keydown.tsPrimeLastToken', (e) => {
      if (e.key === 'Tab' && e.shiftKey) {
        const tokens = this.getTokens();
        if (tokens.length) {
          this.setActiveToken(tokens.last(), { focus: false });
        }
      }
    });
  }

  // ---------- Token Management ----------
  getTokens() {
    return this.tokenBox.find('.token');
  }

  ensureActiveToken(opts = {}) {
    const tokens = this.getTokens();
    if (!tokens.length) return;

    const $active = tokens.filter('[tabindex="0"]').first();
    if ($active.length) return;

    const pick = opts.prefer === 'last' ? tokens.last() : tokens.first();
    this.setActiveToken(pick, { focus: false });
  }

  setActiveToken($token, { focus = false } = {}) {
    this.getTokens().attr('tabindex', '-1');
    $token.attr('tabindex', '0');

    if (focus) {
      $token.trigger('focus');
    }
  }

  enhanceTokenA11y($token) {
    if (!$token || !$token.length) return;

    $token.attr({
      role: 'listitem',
      tabindex: '-1'
    });

    $token.find('.remove-token').attr('tabindex', '-1');
  }

  // ---------- Input Processing ----------
  processInput(e) {
    const value = this.inputNode.val().trim();
    if (!value) return;

    // Enter on KEY (no colon) => append ":" and open values
    if (e.key === 'Enter' && !value.includes(':')) {
      const before = this.inputNode.val();
      this._handleKeyOnlyEnter(e, value);
      if (!e.isDefaultPrevented() && before === this.inputNode.val()) {
        const guess = guessNameParts(value);
        if (guess) {
          this._applyGuessedNameToFields(guess, value);
        }
        e.preventDefault();
        try { this.inputNode.autocomplete('close'); } catch(_) {}
        this._forceSubmit();
      }
      return;
    }

    // Enter/Comma with "key:" and no value => show values, no token
    if ((e.key === 'Enter' || e.key === ',') && /[^:]+:\s*$/.test(value)) {
      e.preventDefault();
      this.inputNode.autocomplete('search', this.inputNode.val());
      return;
    }

    // Enter/Comma with "key:value" => create token
    if ((e.key === 'Enter' || e.key === ',') && value.length) {
      this._handleKeyValueEnter(e, value);
      return;
    }
  }

  _applyGuessedNameToFields(guess, fullText) {
    if (!guess) return;
    if (!this.formNode || !this.formNode.length) return;
    this._clearNameGuessHiddenInputs();

    const addHidden = (fieldName, value) => {
      if (!value) return;
      const safeVal = XssSanitizer.sanitize(value);
      const $hidden = $(`<input type="hidden" class="ts-name-guess" name="${fieldName}" />`).val(safeVal);
      this.formNode.append($hidden);
    };

    const parts = fullText.trim().split(/\s+/).filter(Boolean);
    const singleWord = parts.length === 1;

    if (singleWord) {
      // Heuristic gave only last_name maybe; treat it as first_name
      const only = guess.first_name || guess.last_name || parts[0];
      addHidden('first_name', only);
      // Only add composite name if there is no visible first_name field
      const visibleFirst = this.formNode.find('[name="first_name"]').not('input.ts-name-guess');
      if (!visibleFirst.length) addHidden('name', fullText);
    } else {
      if (guess.first_name) addHidden('first_name', guess.first_name);
      if (guess.last_name) addHidden('last_name', guess.last_name);
      // Provide composite only if no visible first_name
      const visibleFirst = this.formNode.find('[name="first_name"]').not('input.ts-name-guess');
      if (!visibleFirst.length) addHidden('name', fullText);
    }

    this._nameGuessApplied = true;
  }

  _maybeApplyNameGuess() {
    if (this._nameGuessApplied) return false;
    const raw = this.inputNode.val().trim();
    if (!raw || raw.includes(':')) return false; // skip keyed syntax
    const valueLower = raw.toLowerCase();
    const isKnownKey = this.keysForDisplay.some(k => k.label.toLowerCase() === valueLower) || !!this.keyMap[raw];
    if (isKnownKey) return false;
    const guess = guessNameParts(raw);
    if (!guess) return false;
    this._applyGuessedNameToFields(guess, raw);
    return true;
  }

  _forceSubmit() {
    if (!this.formNode || !this.formNode.length) {
      if (this.submitBtn && this.submitBtn.length) {
        this.submitBtn.get(0).click();
      }
      return;
    }
    const formEl = this.formNode.get(0);
    // Prefer requestSubmit (fires validation + submit events properly)
    if (formEl && typeof formEl.requestSubmit === 'function') {
      try { formEl.requestSubmit(); return; } catch (_) {}
    }
    // Fallback to clicking submit button
    if (this.submitBtn && this.submitBtn.length) {
      try { this.submitBtn.get(0).click(); return; } catch (_) {}
    }
    // Final fallback: dispatch a submit event; if not prevented call native submit
    if (formEl) {
      const evt = new Event('submit', { bubbles: true, cancelable: true });
      if (formEl.dispatchEvent(evt)) {
        try { formEl.submit(); } catch (_) {}
      }
    }
  // FINAL HARD FALLBACK: direct native submit (ignores prevented default)
  try { formEl.submit(); } catch(_) {}
  }

  _handleKeyOnlyEnter(e, value) {
    const valueLower = value.toLowerCase();
    const friendly = this.keysForDisplay.find(k => k.label.toLowerCase() === valueLower);
    const realKey = friendly ? friendly.realKey : (this.keyMap[value] ? value : null);

    if (realKey) {
      e.preventDefault();
      this.pendingRealKey = realKey;
      const friendlyLabel = friendly ? friendly.label : (this.keyMap[realKey]?.human || $.humanizeString(realKey));
      this.inputNode.val(`${friendlyLabel}:`);
      safeFocus(this.inputNode);
      this.inputNode.autocomplete('search', this.inputNode.val());
      return true;
    }
    return false;
  }

  _handleKeyValueEnter(e, value) {
    e.preventDefault();
    const match = value.match(/^([^:]+):(.+)$/);

    if (match) {
      const friendlyKey = match[1].trim();
      const typedValLabel = match[2].trim();

      const realKey = this.resolveRealKey(friendlyKey);
      // If it's not a recognized key, don't create a token
      if (!realKey) {
        this.inputNode.val(friendlyKey);
        this.inputNode.autocomplete('search', friendlyKey);
        return;
      }

      const resolvedValue = this.resolveRealValueFromLabel(realKey, typedValLabel) || typedValLabel;

      // Date validation: if key is date-like, only proceed when value parses as a date
  const entry = this.keyMap[realKey];
  if (isDateLikeKeyName(realKey, entry)) {
        if (!isValidDateString(resolvedValue)) {
          // Keep the user in value entry state, show suggestions maybe later
          this.inputNode.addClass('ts-invalid-date');
          // Optionally announce error (simple aria-live could be added outside of this script)
          return; // abort token creation
        } else {
          this.inputNode.removeClass('ts-invalid-date');
        }
      }

      // Compute display labels
      const { displayLabel, displayKeyLabel } = this._computeDisplayLabels(realKey, resolvedValue, typedValLabel);

      // Find the related field
      const $field = this._findFieldForKey(realKey);

      if (['first_name', 'last_name', 'name'].includes(realKey)) {
        this._clearNameGuessHiddenInputs();
        this._ensureExplicitNameHidden(realKey, resolvedValue);
        // Clear any existing free-text search param since explicit name token supersedes it
        this._clearFreeTextHidden();
      }

      if (this._wouldExceedRange(realKey, resolvedValue)) {
        this.inputNode.addClass('ts-date-range-error');
        return;
      } else {
        this.inputNode.removeClass('ts-date-range-error');
      }

      const tok = new SearchToken({
        key: realKey,
        value: resolvedValue,
        field: $field.length ? $field : this.inputNode,
        source: 'input',
        container: this.tokenBox,
        displayLabel,
        displayKeyLabel
      });

      this.enhanceTokenA11y($(tok.node));
      this.pendingRealKey = null;
      this.inputNode.val('');
      // For explicit name tokens, do not refocus; requirement: just clear the input.
      if (!['first_name','last_name','name'].includes(realKey)) {
        this.inputNode.trigger('focus');
      }
      if (['first_name','last_name','name'].includes(realKey)) {
        // Ensure hidden search_box input removed and input cleared triggers listeners
        this._clearFreeTextHidden();
        this.inputNode.trigger('input');
      }
    }
  }

  _computeDisplayLabels(realKey, resolvedValue, typedValLabel) {
    let displayLabel = typedValLabel;
    const entry = this.keyMap[realKey];

    if (entry) {
      const t = (entry.type || '').toLowerCase();

      if (t === 'checkbox') {
        const b = booleanFrom(resolvedValue);
        if (b === true) {
          const checkboxValueEntry = entry.values.find(v => v.value === 'true');
          displayLabel = checkboxValueEntry ? checkboxValueEntry.label : 'Yes';
        } else if (b === false) {
          displayLabel = 'No';
        }
      } else if (t === 'select-one' || t === 'radio') {
        const hit = entry.values.find(v => String(v.value) === String(resolvedValue));
        if (hit) displayLabel = hit.label;
      }
    }

    const displayKeyLabel = (this.keyMap[realKey]?.human) || $.humanizeString(realKey);

    return { displayLabel, displayKeyLabel };
  }

  // ---------- Advanced Panel Sync ----------
  tokenizeFormSync(event) {
    const target = this._resolveEventTarget(event);
    if (!target) return;

    const field = $(target);
    if (!field.closest(this.panelNode).length) return;

    // During name guess application we don't want to emit visible tokens for these underlying fields
    if (this._suppressNameGuessTokensDuringCycle) {
      const nm = (field.attr('name') || '').toLowerCase();
      if (nm === 'first_name' || nm === 'last_name' || nm === 'name') {
        return; // skip token creation
      }
    }

    const name = (field.attr('name') || '').trim();
    const lname = name.toLowerCase();

    if (['first_name','last_name','name'].includes(lname)) {
      this._clearNameGuessHiddenInputs();
  // Keep a hidden explicit mirror so submission always includes latest value
  this._ensureExplicitNameHidden(lname, field.val());
    }

    const typeAttr = (field.attr('type') || '').toLowerCase();
    const nodeName = field[0].nodeName.toLowerCase();

    // Skip system fields
    if (typeAttr === 'hidden' ||
        (nodeName === 'input' && ['hidden', 'submit', 'button', 'image'].includes(typeAttr))) {
      return;
    }

    if (['utf8', 'authenticity_token', 'commit'].includes(lname)) {
      return;
    }

    const type = nodeName === 'select' ? 'select-one' : typeAttr;
    const key = name || 'search';

    // Remove existing token for this key (scoped)
    this.tokenBox.find(`.token[data-key="${key.toLowerCase()}"]`).remove();

    let displayKeyLabel = getFieldLabel(field, this.formNode);

    if (type === 'radio') {
      displayKeyLabel = this._getRadioGroupLabel(field);
    }

    // Handle different field types
    switch (type) {
      case 'checkbox':
        this._handleCheckboxSync(field, key, displayKeyLabel);
        break;

      case 'radio':
        this._handleRadioSync(field, key, name, displayKeyLabel);
        break;

      case 'select-one':
        this._handleSelectSync(field, key, displayKeyLabel);
        break;

      default:
        this._handleTextSync(field, key, displayKeyLabel);
    }
  }

  _resolveEventTarget(event) {
    return event?.target && event.target.nodeType
      ? event.target
      : (event?.target && event.target[0])
      ? event.target[0]
      : event?.target || null;
  }

  _getRadioGroupLabel(field) {
    // Prefer the group label for the shared name (e.g., "Delegate Status")
    const nameForRadio = field.attr('name') || '';
    const $grp = field.closest('.form-group, .form-group.row, .row');

    if ($grp.length) {
      // Look for any label in the group that's not for a specific radio button
      const $labels = $grp.find('label');
      for (let i = 0; i < $labels.length; i++) {
        const $lab = $($labels[i]);
        const forAttr = $lab.attr('for');

        // If the label's 'for' attribute matches the radio name exactly, or has no 'for' attribute, it's likely the group label
        if (!forAttr || forAttr === nameForRadio) {
          const text = $lab.text().trim();
          if (text) return normalizeKeyLabel(text);
        }
      }
    }

    const $formLab = this.formNode.find(`label[for="${nameForRadio}"]`).first();
    if ($formLab.length && $formLab.text().trim()) {
      return normalizeKeyLabel($formLab.text().trim());
    }

    return getFieldLabel(field, this.formNode);
  }

  _handleCheckboxSync(field, key, displayKeyLabel) {
    if (field.is(':checked')) {
      // Use centralized checkbox label logic
      const displayLabel = getCheckboxLabel(field, displayKeyLabel);

      const tok = new SearchToken({
        key,
        value: 'true',
        field,
        source: 'advanced',
        container: this.tokenBox,
        displayLabel,
        displayKeyLabel
      });
      this.enhanceTokenA11y($(tok.node));
    }
  }

  _handleRadioSync(field, key, name, displayKeyLabel) {
    const $checked = this.panelNode.find(`input[type="radio"][name="${name}"]:checked`);
    if ($checked.length) {
      const tok = new SearchToken({
        key,
        value: $checked.val(),
        field: $checked,
        source: 'advanced',
        container: this.tokenBox,
        displayKeyLabel
      });
      this.enhanceTokenA11y($(tok.node));
    }
  }

  _handleSelectSync(field, key, displayKeyLabel) {
    const val = field.val();
    if (val && String(val).length) {
      const entry = this.keyMap[key];
  if (isDateLikeKeyName(key, entry) && !isValidDateString(val)) {
        return;
      }
      const $opt = field.find(`option[value="${val}"]`);
      const disp = $opt.length ? $opt.text().trim() : val;
      const tok = new SearchToken({
        key,
        value: val,
        field,
        source: 'advanced',
        container: this.tokenBox,
        displayLabel: disp,
        displayKeyLabel
      });
      this.enhanceTokenA11y($(tok.node));
  this._enforceThresholdDateRange();
    }
  }

  _handleTextSync(field, key, displayKeyLabel) {
    const val = (field.val() || '').toString().trim();
    if (val.length > 0) {
      const entry = this.keyMap[key];
  if (isDateLikeKeyName(key, entry) && !isValidDateString(val)) {
        return;
      }
      if (isDateLikeKeyName(key, entry) && this._wouldExceedRange(key, val)) {
        this.inputNode.addClass('ts-date-range-error');
        return;
      } else if (isDateLikeKeyName(key, entry)) {
        this.inputNode.removeClass('ts-date-range-error');
      }
      const tok = new SearchToken({
        key,
        value: val,
        field,
        source: 'advanced',
        container: this.tokenBox,
        displayKeyLabel
      });
      this.enhanceTokenA11y($(tok.node));
      if (['first_name','last_name','name'].includes(key)) {
        // Explicit name input in panel: clear free-text box & its hidden param
        this.inputNode.val('');
        this._clearFreeTextHidden();
        this.inputNode.trigger('input');
      }
    }
  }

  // -------- Threshold Range (dynamic) --------
  _thresholdMaxDays() {
    if (this._cachedMaxDays) return this._cachedMaxDays;
    let max = 100; // fallback
    if (this.panelNode && this.panelNode.length) {
      const attr = this.panelNode.attr('data-threshold-max-days');
      if (attr && !isNaN(Number(attr))) max = Number(attr);
    }
    this._cachedMaxDays = max;
    return max;
  }

  _currentDates() {
    const beginTok = this.tokenBox.find('.token[data-key="begin_date"], .token[data-key="request_begin_date"], .token[data-key="threshold_begin_date"]').first();
    const endTok = this.tokenBox.find('.token[data-key="end_date"], .token[data-key="request_end_date"], .token[data-key="threshold_end_date"]').first();
    const bVal = beginTok.attr('data-human-value');
    const eVal = endTok.attr('data-human-value');
    const b = parseMMDDYYYY(bVal);
    const e = parseMMDDYYYY(eVal);
    return { b, e };
  }

  _wouldExceedRange(newKey, newVal) {
    const isBegin = /(^|_)begin_date$/.test(newKey);
    const isEnd = /(^|_)end_date$/.test(newKey);
    if (!isBegin && !isEnd) return false;
    const { b, e } = this._currentDates();
    const max = this._thresholdMaxDays();
    let beginDate = b;
    let endDate = e;
    const candidate = parseMMDDYYYY(newVal);
    if (!candidate) return false; // invalid already handled upstream
    if (isBegin) beginDate = candidate; else endDate = candidate;
    if (beginDate && endDate) {
      const diff = (endDate - beginDate) / 86400000;
      if (diff > max || diff < 0) return true;
    }
    return false;
  }
}

// ------------- Name Guess Helpers -------------
TokenizedSearch.prototype._clearNameGuessHiddenInputs = function() {
  if (!this.formNode || !this.formNode.length) return;
  this.formNode.find('input[type="hidden"].ts-name-guess[name="first_name"], input[type="hidden"].ts-name-guess[name="last_name"], input[type="hidden"].ts-name-guess[name="name"]').remove();
  this._nameGuessApplied = false;
};

TokenizedSearch.prototype._ensureExplicitNameHidden = function(fieldName, value) {
  if (!this.formNode || !this.formNode.length) return;
  if (value == null) return;
  // Prefer updating an existing non-guess visible field
  const $visible = this.formNode.find(`[name="${fieldName}"]`).not('.ts-name-guess');
  if ($visible.length) {
    $visible.first().val(XssSanitizer.sanitize(value));
    return;
  }
  // Otherwise create/update an explicit hidden mirror (not marked as guess)
  let $hidden = this.formNode.find(`input[type="hidden"].ts-name-explicit[name="${fieldName}"]`);
  if (!$hidden.length) {
    $hidden = $(`<input type="hidden" class="ts-name-explicit" name="${fieldName}" />`);
    this.formNode.append($hidden);
  }
  $hidden.val(XssSanitizer.sanitize(value));
};

TokenizedSearch.prototype._clearFreeTextHidden = function() {
  if (!this.formNode || !this.formNode.length) return;
  this.formNode.find('input[type="hidden"][name="search_box"]').remove();
};

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const $inputs = $('#search_box');
  if ($inputs.length) {
    $inputs.each((_, el) => new TokenizedSearch(el));
  }
});
